import { observer } from 'mobx-react'
import { useNavigate } from 'react-router-dom'
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { Container } from '../../components/Animations'
import { PrimaryButton, SecondaryLinkButton } from '../../components/Button'
import {
  DashboardPage,
  EmptyState,
  InlineNotice,
  InlinePill,
  PageActionRow,
  PageHeaderRow,
  Surface,
  SurfaceHeader,
  SurfaceSubtext,
  SurfaceTitle
} from '../../components/DashboardPrimitives'
import { DropdownContext, DropdownItem, DropdownSearchContainer, DropdownTextInput } from '../../components/Dropdown'
import { TextInput } from '../../components/Input'
import LoadingBar from '../../components/LoadingBar'
import PageHeader from '../../components/PageHeader'
import PaymentMethodDisplay from '../../components/PaymentMethodDisplay'
import { format as formatDate } from 'date-fns'
import { Chevron } from '../../icons/Chevron'
import FiatRamps from '../../state/FiatRamps'
import Radom, {
  Customer,
  ExternalAccount,
  ICreateCustomer,
  Payout,
  PayoutAmountMode,
  PayoutFeeEstimate,
  PayoutFundingCurrency,
  WithdrawalAccount,
  WithdrawalRequestWithAccountName
} from '../../state/Radom'
import Treasury from '../../state/Treasury'
import User from '../../state/User'
import { isCryptoExternalAccount, isEurIbanExternalAccount } from '../../util/ExternalAccounts'
import { getMethod, prodNetworks, testNetworks, unifiedAssetNetworkOptions } from '../../util/Managed'
import { payoutStatusLabel } from '../../util/PayoutStatus'
import { ROUTES, errorToast, formatAssetAmount, shortAddress, successToast, useTestMode } from '../../util/Util'
import { usePermissions } from '../../util/permissions'
import CreateWithdrawalAccountModal, { AccountType } from '../Funds/CreateWithdrawalAccountModal'
import DestinationPicker from './DestinationPicker'
import EscapableDropdown from './EscapableDropdown'
import RecipientAvatar from './RecipientAvatar'
import {
  BackTextButton,
  BlockerNotice,
  CenteredWorkspace,
  FlowSurface,
  HeroAmountField,
  HeroAmountRow,
  HeroCurrencySuffix,
  HeroHelperRow,
  LightSegmented,
  LightSegmentedButton,
  MaxTextButton,
  QuoteBox,
  QuoteLabel,
  QuoteLineButton,
  QuoteRow,
  QuoteValue,
  ReviewAmount,
  ReviewDetail,
  ReviewIdentity,
  ReviewName,
  SectionLabel,
  SelectionLabel,
  SelectionMeta,
  SelectionSubtext,
  SendLayout,
  SimpleMethodIcon,
  StepReveal,
  SuccessBadge,
  SuccessHeading,
  SuccessNote,
  SuccessPanel,
  SuccessSummary,
  ActivityRowLink,
  QuickActionsRow,
  QuickActionPill,
  QuickActionGlyph,
  RecipientCard,
  RecipientCardTitle,
  RecipientCardHint,
  RecipientFieldsGrid
} from './SendStyles'
import {
  OneOffKind,
  SendDestination,
  destinationDisplayName,
  destinationKey,
  destinationMode,
  networkAcceptsOneOffKind,
  oneOffKindLabel,
  oneOffNetworkCandidates,
  withdrawalAccountSubtitle
} from './sendUtil'

/** Which panel occupies the form surface: edit, confirm, or post-send. */
type SendStep = 'form' | 'review' | 'success'

interface FundingAsset {
  network: string
  token?: string
  maxAmount: string
  treasuryId?: string
}

const fundingAssetKey = (asset: FundingAsset): string =>
  `${asset.network}:${asset.token ?? 'native'}:${asset.treasuryId ?? 'radom'}`

/**
 * The concrete rails a selected destination resolves to. Own accounts book as
 * withdrawals (crypto rail, the pooled EUR cash rail, or the legacy cash
 * rail); customers and one-off addresses book as payouts.
 */
type SendFlow =
  | { kind: 'idle' }
  | { kind: 'blocked', reason: string }
  | { kind: 'customerPending', customer: Customer }
  | { kind: 'cryptoWithdrawal', account: WithdrawalAccount, network: string }
  | { kind: 'ivyCashWithdrawal', account: WithdrawalAccount }
  | { kind: 'legacyCashWithdrawal', account: WithdrawalAccount, currency: 'EUR' | 'USD' }
  | {
    kind: 'cryptoPayout'
    payoutAddress: string
    requiredNetwork?: string
    oneOffKind?: OneOffKind
    customer?: Customer
    externalAccount?: ExternalAccount
  }
  | { kind: 'fiatPayout', customer: Customer, externalAccount: ExternalAccount }
  | { kind: 'ibanRecipient', iban: string }
  | { kind: 'walletRecipient', address: string, candidates: string[] }

interface ActivityRow {
  id: string
  title: string
  meta: string
  amountLabel: string
  statusLabel: string
  tone: 'green' | 'amber' | 'red' | 'blue' | 'neutral'
  timestamp: number
  href: string
  method?: { network: string, token?: string }
}

/** Everything the in-place success panel needs after the form resets. */
interface SuccessInfo {
  heading: string
  amountLabel: string
  recipientName: string
  recipientDetail: string
  note: string
  historyHref: string
  historyLabel: string
}

const VOLATILE_CASH_FUNDING: Array<{ ticker: PayoutFundingCurrency, network: string }> = [
  { ticker: 'BTC', network: 'Bitcoin' },
  { ticker: 'ETH', network: 'Ethereum' },
  { ticker: 'SOL', network: 'Solana' },
  { ticker: 'POL', network: 'Polygon' },
  { ticker: 'AVAX', network: 'Avalanche' },
  { ticker: 'LTC', network: 'Litecoin' },
  { ticker: 'DOGE', network: 'Dogecoin' }
]

const formatQuoteAmount = (value?: number | string | null, decimals = 6): string => {
  if (value === undefined || value === null || value === '') return '-'
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return String(value)
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals
  })
}

const externalAccountLabel = (account: ExternalAccount): string => {
  if (account.crypto) {
    return `${account.crypto.network} wallet · ${shortAddress(account.crypto.address, 6)}`
  }
  if (account.iban) {
    return `SEPA · IBAN ····${account.iban.last4}`
  }
  return account.bankName || 'Bank account'
}

const withdrawalStatusTone = (status: string): ActivityRow['tone'] =>
  status === 'complete'
    ? 'green'
    : status === 'failed'
      ? 'red'
      : status === 'processing' ? 'blue' : 'amber'

/** "pendingApproval" → "Pending approval" for the activity rail. */
const withdrawalStatusLabel = (status: string): string => {
  if (status === 'awaitingInventory') {
    // Unified-treasury wait state; render white-label copy, not the backend name.
    return 'Awaiting funds'
  }
  const spaced = status.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

const payoutStatusTone = (status: Payout['status']): ActivityRow['tone'] => {
  switch (status) {
  case 'Complete':
    return 'green'
  case 'Failed':
  case 'Expired':
    return 'red'
  case 'Processing':
  case 'Pending':
    return 'blue'
  default:
    return 'amber'
  }
}

const CheckMark = (): ReactElement => (
  <svg viewBox='0 0 24 24' width='26' height='26' aria-hidden='true'>
    <path
      d='m5 12.5 4.5 4.5L19 7.5'
      fill='none'
      stroke='currentColor'
      strokeWidth='2.4'
      strokeLinecap='round'
      strokeLinejoin='round' />
  </svg>
)

const Send = observer((): ReactElement => {
  const [testMode] = useTestMode()
  const { has, isLoaded: permissionsLoaded } = usePermissions()
  const navigate = useNavigate()

  const canReadBalance = has('ReadBalance')
  const canReadAccounts = has('ReadWithdrawalAccount')
  const canWriteAccounts = has('WriteWithdrawalAccount')
  const canReadCustomers = has('ReadCustomers')
  const canReadExternalAccounts = has('ReadExternalAccount')
  const canSaveIbanRecipients = has('WriteCustomers', 'WriteExternalAccount', 'ReadExternalAccount')
  const canReadWithdrawals = has('ReadWithdrawal')
  const canReadPayouts = has('ReadPayouts')
  const canWriteWithdrawal = has('WriteWithdrawal')
  const canWritePayouts = has('WritePayouts')
  const canUseView = canWriteWithdrawal || canWritePayouts || canReadWithdrawals || canReadPayouts

  const [step, setStep] = useState<SendStep>('form')
  const [successInfo, setSuccessInfo] = useState<SuccessInfo>()
  const [destination, setDestination] = useState<SendDestination>()
  // Which in-flow "save a destination" modal is open, if any. Checked against
  // undefined everywhere: AccountType.Crypto is 0 and would fail truthiness.
  const [createAccountType, setCreateAccountType] = useState<AccountType>()
  const [accounts, setAccounts] = useState<WithdrawalAccount[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(false)
  const [externalAccounts, setExternalAccounts] = useState<ExternalAccount[]>([])
  const [isLoadingExternalAccounts, setIsLoadingExternalAccounts] = useState(false)
  const [selectedExternalAccountId, setSelectedExternalAccountId] = useState<string>()
  const [selectedFundingKey, setSelectedFundingKey] = useState<string>()
  const [assetSearch, setAssetSearch] = useState('')
  const [amount, setAmount] = useState('')
  const [amountMode, setAmountMode] = useState<PayoutAmountMode>('recipient')
  // One-off recipients have no customer record; the payout API mints one from
  // the email, and it is the first of the travel-rule identity fields.
  const [oneOffEmail, setOneOffEmail] = useState('')
  const [oneOffName, setOneOffName] = useState('')
  // Inline save-recipient form for pasted one-off IBANs.
  const [recipientOwnerType, setRecipientOwnerType] = useState<'individual' | 'business'>('individual')
  const [recipientFirstName, setRecipientFirstName] = useState('')
  const [recipientLastName, setRecipientLastName] = useState('')
  const [recipientBusinessName, setRecipientBusinessName] = useState('')
  const [recipientEmail, setRecipientEmail] = useState('')
  const [recipientBic, setRecipientBic] = useState('')
  const [isSavingRecipient, setIsSavingRecipient] = useState(false)
  const [feeEstimate, setFeeEstimate] = useState<PayoutFeeEstimate | null>(null)
  const [isEstimatingFee, setIsEstimatingFee] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [quoteExpanded, setQuoteExpanded] = useState(false)
  const [recentWithdrawals, setRecentWithdrawals] = useState<WithdrawalRequestWithAccountName[]>([])
  const [recentPayouts, setRecentPayouts] = useState<Payout[]>([])
  const [isLoadingActivity, setIsLoadingActivity] = useState(false)

  const amountInputRef = useRef<HTMLInputElement | null>(null)
  const pendingAmountFocus = useRef(false)
  const reviewActionsRef = useRef<HTMLDivElement | null>(null)
  const savedRecipientCustomer = useRef<{ email: string, customer: Customer } | null>(null)
  const [walletRecipientName, setWalletRecipientName] = useState('')
  const [walletRecipientNetwork, setWalletRecipientNetwork] = useState<string | undefined>(undefined)

  // One key per logical cash submission: stable across retries after an error
  // (so a timed-out request replays instead of double-paying), fresh after
  // success. Mirrors the existing withdrawal modal behavior.
  const cashSubmissionKey = useRef(crypto.randomUUID())

  const usesLegacyEurOfframp = User.organization?.legacyEurOfframpEnabled === true

  /** Loads both recipient lists; returns the fresh accounts so callers can
   *  pick out a just-created one without a second fetch. */
  const loadRecipients = async (): Promise<WithdrawalAccount[]> => {
    setIsLoadingRecipients(true)
    const networks = testMode ? testNetworks : prodNetworks
    const [accountsResult, customersResult] = await Promise.allSettled([
      canReadAccounts ? Radom.listWithdrawalAccounts() : Promise.resolve([] as WithdrawalAccount[]),
      canReadCustomers
        ? Radom.listCustomers(0, 100, networks)
        : Promise.resolve({ data: [] as Customer[], total: 0 })
    ])

    let loadedAccounts: WithdrawalAccount[] = []
    if (accountsResult.status === 'fulfilled') {
      loadedAccounts = accountsResult.value
      setAccounts(loadedAccounts)
    } else {
      console.error('Failed to load withdrawal accounts', accountsResult.reason)
      setAccounts([])
    }

    if (customersResult.status === 'fulfilled') {
      setCustomers(customersResult.value.data)
    } else {
      console.error('Failed to load customers', customersResult.reason)
      setCustomers([])
    }

    setIsLoadingRecipients(false)
    return loadedAccounts
  }

  // In-flow account creation: once the modal saves, the new account becomes
  // the selected destination so the user lands back mid-flow rather than at
  // an empty picker. The modal doesn't hand back the created entity, so the
  // reloaded list is diffed against the ids known beforehand, falling back to
  // the newest account by creation date.
  const handleAccountCreated = async (): Promise<void> => {
    const knownIds = new Set(accounts.map(a => a.id))
    setCreateAccountType(undefined)
    const freshAccounts = await loadRecipients()
    const newestFirst = [...freshAccounts].sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    const created = newestFirst.find(a => !knownIds.has(a.id)) ?? newestFirst[0]
    if (created) {
      pendingAmountFocus.current = true
      setDestination({ kind: 'ownAccount', account: created })
    }
  }

  /**
   * Saves a pasted one-off IBAN as a recipient: a customer record plus an
   * external bank account under it. On success the customer becomes the
   * destination, so the flow continues straight into the amount step with the
   * new account as the delivery target.
   */
  const saveIbanRecipient = async (iban: string): Promise<void> => {
    if (isSavingRecipient) return

    const firstName = recipientFirstName.trim()
    const lastName = recipientLastName.trim()
    const businessName = recipientBusinessName.trim()
    const email = recipientEmail.trim()
    const ownerName = recipientOwnerType === 'business'
      ? businessName
      : `${firstName} ${lastName}`.trim()

    if (recipientOwnerType === 'individual' && (!firstName || !lastName)) {
      errorToast('Enter the recipient’s first and last name')
      return
    }
    if (recipientOwnerType === 'business' && !businessName) {
      errorToast('Enter the business name on the bank account')
      return
    }
    if (!email.includes('@')) {
      errorToast('Enter the recipient’s email address')
      return
    }

    setIsSavingRecipient(true)
    try {
      // A failed account save (e.g. bank verification) shouldn't mint a
      // duplicate customer when the user retries.
      let customer = savedRecipientCustomer.current?.email === email
        ? savedRecipientCustomer.current.customer
        : undefined
      if (!customer) {
        // The create endpoint only needs name and email; the full
        // ICreateCustomer shape is a legacy Omit of the read model.
        const newCustomer: Pick<ICreateCustomer, 'name' | 'email'> = { name: ownerName, email }
        customer = await Radom.createCustomer(newCustomer as ICreateCustomer)
        savedRecipientCustomer.current = { email, customer }
      }
      const bic = recipientBic.trim().toUpperCase()
      await Radom.createExternalAccount({
        customer_id: customer.id,
        currency: 'eur',
        account_type: 'iban',
        account_owner_name: ownerName,
        account_owner_type: recipientOwnerType,
        ...(recipientOwnerType === 'individual'
          ? { first_name: firstName, last_name: lastName }
          : { business_name: businessName }),
        iban: {
          account_number: iban,
          ...(bic ? { bic } : {}),
          country: iban.slice(0, 2).toUpperCase()
        }
      })
      successToast('Recipient saved')
      void loadRecipients()
      pendingAmountFocus.current = true
      setDestination({ kind: 'customer', customer })
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsSavingRecipient(false)
  }

  /**
   * Saves a pasted one-off crypto address as a wallet withdrawal account and
   * makes it the destination, so the flow continues straight into the amount
   * step on the withdrawal rail (the only rail covering every network).
   */
  const saveWalletRecipient = async (address: string): Promise<void> => {
    if (isSavingRecipient) return

    const name = walletRecipientName.trim()
    if (!walletRecipientNetwork) {
      errorToast('Choose the network for this address')
      return
    }
    if (!name) {
      errorToast('Enter a name for this wallet')
      return
    }

    setIsSavingRecipient(true)
    try {
      const knownIds = new Set(accounts.map(a => a.id))
      await Radom.createWithdrawalAccount({
        name,
        accountDetails: {
          Crypto: {
            network: walletRecipientNetwork,
            address
          }
        }
      })
      const freshAccounts = await loadRecipients()
      const created = freshAccounts.find(a =>
        !knownIds.has(a.id) && a.accountDetails.Crypto?.address === address) ??
        freshAccounts.find(a => !knownIds.has(a.id))
      successToast('Wallet saved')
      if (created) {
        pendingAmountFocus.current = true
        setDestination({ kind: 'ownAccount', account: created })
      }
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsSavingRecipient(false)
  }

  const loadActivity = async (): Promise<void> => {
    setIsLoadingActivity(true)
    const [withdrawalsResult, payoutsResult] = await Promise.allSettled([
      canReadWithdrawals
        ? Radom.listWithdrawals(0, 6, testMode ? testNetworks : prodNetworks)
        : Promise.resolve({ data: [] as WithdrawalRequestWithAccountName[], total: 0 }),
      canReadPayouts
        ? Radom.listPayouts(0, 6, testMode, [])
        : Promise.resolve({ data: [] as Payout[], total: 0 })
    ])

    setRecentWithdrawals(withdrawalsResult.status === 'fulfilled' ? withdrawalsResult.value.data : [])
    setRecentPayouts(payoutsResult.status === 'fulfilled' ? payoutsResult.value.data : [])
    if (withdrawalsResult.status === 'rejected') {
      console.error('Failed to load recent withdrawals', withdrawalsResult.reason)
    }
    if (payoutsResult.status === 'rejected') {
      console.error('Failed to load recent payouts', payoutsResult.reason)
    }
    setIsLoadingActivity(false)
  }

  useEffect(() => {
    if (!permissionsLoaded) return
    if (canReadBalance) {
      void Treasury.loadBalances()
    }
    void loadRecipients()
    void loadActivity()
  }, [permissionsLoaded, testMode])

  // Selecting a destination resets the details step; customers additionally
  // load their saved delivery accounts.
  useEffect(() => {
    setAmount('')
    setOneOffEmail('')
    setOneOffName('')
    setRecipientOwnerType('individual')
    setRecipientFirstName('')
    setRecipientLastName('')
    setRecipientBusinessName('')
    setRecipientEmail('')
    setRecipientBic('')
    setAmountMode('recipient')
    setFeeEstimate(null)
    setQuoteExpanded(false)
    setSelectedFundingKey(undefined)
    setSelectedExternalAccountId(undefined)
    setExternalAccounts([])

    if (destination?.kind === 'customer' && canReadExternalAccounts) {
      setIsLoadingExternalAccounts(true)
      Radom.listExternalAccounts(destination.customer.id)
        .then(loaded => setExternalAccounts(loaded))
        .catch(err => {
          console.error('Failed to load customer accounts', err)
          setExternalAccounts([])
        })
        .finally(() => setIsLoadingExternalAccounts(false))
    }
  }, [destination])

  // Keyboard flow: once a destination is picked the amount input takes focus,
  // as soon as it exists (customers first resolve a delivery account).
  useEffect(() => {
    if (pendingAmountFocus.current && amountInputRef.current) {
      pendingAmountFocus.current = false
      amountInputRef.current.focus()
    }
  })

  // Review panel: focus the Send button so Enter submits, Escape goes back.
  useEffect(() => {
    if (step !== 'review') return

    reviewActionsRef.current?.querySelector('button')?.focus()
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !isSubmitting) {
        pendingAmountFocus.current = true
        setStep('form')
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [step, isSubmitting])

  const fundingAssets = useMemo((): FundingAsset[] => {
    const all = [...Treasury.managedBalances, ...Treasury.privateManagedBalances]
    return all
      .filter(b => Number(b.balance) > 0)
      .flatMap((b): FundingAsset[] => {
        // Unified-treasury rows carry an asset ticker and no network, but a
        // send still settles on a concrete rail: expand one unified row into
        // one funding option per settlement network the registry knows for
        // that asset. Every option carries the full unified balance as
        // maxAmount; the backend enforces the real spendable cap when the
        // send executes.
        if (b.asset) {
          if (testMode) return []
          return unifiedAssetNetworkOptions(b.asset)
            .map(option => ({ ...option, maxAmount: b.balance, treasuryId: b.treasuryId }))
        }
        if (b.network === undefined) return []
        const method = getMethod(b.network, b.token)
        if (b.network === 'Fiat' || method.isFiat) return []
        if (testMode ? !method.isTestnet : !!method.isTestnet) return []
        return [{ network: b.network, token: b.token, maxAmount: b.balance, treasuryId: b.treasuryId }]
      })
  }, [Treasury.managedBalances, Treasury.privateManagedBalances, testMode])

  const deliveryOptions = useMemo(
    () => externalAccounts.filter(a => isCryptoExternalAccount(a) || isEurIbanExternalAccount(a)),
    [externalAccounts]
  )
  const selectedExternalAccount = deliveryOptions.find(a => a.id === selectedExternalAccountId)

  // Auto-pick the only delivery option so a single-account customer needs no
  // extra click.
  useEffect(() => {
    if (destination?.kind === 'customer' && !selectedExternalAccountId && deliveryOptions.length === 1) {
      setSelectedExternalAccountId(deliveryOptions[0].id)
    }
  }, [deliveryOptions, destination, selectedExternalAccountId])

  const flow = useMemo((): SendFlow => {
    if (!destination) return { kind: 'idle' }

    if (destination.kind === 'ownAccount') {
      const account = destination.account
      const crypto = account.accountDetails.Crypto
      if (crypto) {
        return { kind: 'cryptoWithdrawal', account, network: crypto.network }
      }

      const fiat = account.accountDetails.Fiat
      const isIban = !!fiat?.accountType.iban
      const isUs = !!fiat?.accountType.us

      // Business and individually-owned IBANs both settle through the
      // provider; the backend routes them over the matching rail.
      if (isIban && !usesLegacyEurOfframp) {
        if (!FiatRamps.cashWithdrawalEnabled) {
          return {
            kind: 'blocked',
            reason: 'Cash withdrawals aren’t enabled for your organization yet. Contact support to switch them on.'
          }
        }
        return { kind: 'ivyCashWithdrawal', account }
      }

      // Saved US account-and-routing destinations predate the USD rail. The
      // legacy USD off-ramp is retired, so submitting would only surface a
      // provider error; hold them until the USD rail is live.
      if (isUs) {
        return {
          kind: 'blocked',
          reason: 'USD bank withdrawals are coming soon. This saved account will work as soon as the USD rail is live.'
        }
      }

      if (isIban) {
        if (!FiatRamps.cashWithdrawalEnabled) {
          return {
            kind: 'blocked',
            reason: 'Cash withdrawals aren’t enabled for your organization yet. Contact support to switch them on.'
          }
        }
        return { kind: 'legacyCashWithdrawal', account, currency: 'EUR' }
      }

      return { kind: 'blocked', reason: 'This saved account type isn’t supported for sending yet.' }
    }

    if (destination.kind === 'customer') {
      const customer = destination.customer
      if (!canReadExternalAccounts) {
        return {
          kind: 'blocked',
          reason: 'Your role can’t read contact accounts, so there’s nowhere to deliver this payout. Ask an admin for the ReadExternalAccount permission.'
        }
      }
      if (isLoadingExternalAccounts) {
        return { kind: 'customerPending', customer }
      }
      if (deliveryOptions.length === 0) {
        return {
          kind: 'blocked',
          reason: externalAccounts.length > 0
            ? 'This contact’s saved accounts aren’t supported yet. Payouts currently deliver to EUR IBANs and crypto wallets.'
            : 'This contact has no saved bank account or wallet yet. Add one from their page under Contacts, then come back.'
        }
      }
      if (!selectedExternalAccount) {
        return { kind: 'customerPending', customer }
      }
      if (selectedExternalAccount.crypto) {
        return {
          kind: 'cryptoPayout',
          payoutAddress: selectedExternalAccount.crypto.address,
          requiredNetwork: selectedExternalAccount.crypto.network,
          customer,
          externalAccount: selectedExternalAccount
        }
      }
      return { kind: 'fiatPayout', customer, externalAccount: selectedExternalAccount }
    }

    // One-off destinations. A pasted IBAN can't pay out anonymously — it
    // delivers as a payout to a saved recipient, so offer to save one inline.
    if (destination.oneOffKind === 'iban') {
      if (canSaveIbanRecipients) {
        return { kind: 'ibanRecipient', iban: destination.value }
      }
      return {
        kind: 'blocked',
        reason: 'Bank payouts deliver to saved recipients, and your role can’t create them. Ask an admin for the WriteCustomers and WriteExternalAccount permissions.'
      }
    }

    // Networks outside the payout engine (Bitcoin-family, Cardano, Polkadot)
    // settle as withdrawals, and withdrawals only deliver to saved accounts.
    if (destination.oneOffKind === 'other') {
      if (!canWriteAccounts) {
        return {
          kind: 'blocked',
          reason: 'Sending to this address books as a withdrawal to a saved wallet account, and your role can\u2019t create one. Ask an admin for the WriteWithdrawalAccount permission.'
        }
      }
      return {
        kind: 'walletRecipient',
        address: destination.value,
        candidates: oneOffNetworkCandidates(destination.value)
      }
    }

    return {
      kind: 'cryptoPayout',
      payoutAddress: destination.value,
      oneOffKind: destination.oneOffKind
    }
  }, [
    destination,
    canWriteAccounts,
    usesLegacyEurOfframp,
    FiatRamps.cashWithdrawalEnabled,
    canReadExternalAccounts,
    isLoadingExternalAccounts,
    deliveryOptions,
    externalAccounts.length,
    selectedExternalAccount,
    canSaveIbanRecipients
  ])

  const expectedUsdcNetwork = testMode ? 'SepoliaTestnet' : 'Ethereum'

  const eligibleFundingAssets = useMemo((): FundingAsset[] => {
    switch (flow.kind) {
    case 'cryptoWithdrawal':
      return fundingAssets.filter(a => a.network === flow.network)
    case 'legacyCashWithdrawal':
      return fundingAssets.filter(a => !a.treasuryId)
    case 'ivyCashWithdrawal':
      return fundingAssets.filter(a => {
        if (a.treasuryId) return false
        const method = getMethod(a.network, a.token)
        const isUsdc = a.network === expectedUsdcNetwork && method.ticker === 'USDC'
        const isUsdt = !testMode && a.network === 'Ethereum' && method.ticker === 'USDT'
        const isBscStable = !testMode && a.network === 'BNB' &&
          (method.ticker === 'USDC' || method.ticker === 'USDT')
        const isVolatile = !testMode && !a.token &&
          VOLATILE_CASH_FUNDING.some(v => v.network === a.network && v.ticker === method.ticker)
        return isUsdc || isUsdt || isBscStable || isVolatile
      })
    case 'cryptoPayout':
      return fundingAssets
        .filter(a => !a.treasuryId)
        .filter(a => {
          if (flow.requiredNetwork) {
            return a.network === flow.requiredNetwork
          }
          if (flow.oneOffKind) {
            const method = getMethod(a.network, a.token)
            return networkAcceptsOneOffKind(flow.oneOffKind, a.network, method.chainId)
          }
          return false
        })
    default:
      return []
    }
  }, [flow, fundingAssets, expectedUsdcNetwork, testMode])

  // Seed the save-wallet card per pasted address: single-candidate networks
  // preselect, and the nickname proposes itself from the network's ticker.
  useEffect(() => {
    if (flow.kind !== 'walletRecipient') return
    const network = flow.candidates.length === 1 ? flow.candidates[0] : undefined
    setWalletRecipientNetwork(network)
    setWalletRecipientName(network ? `${getMethod(network).ticker} wallet` : '')
  }, [flow.kind === 'walletRecipient' ? flow.address : undefined])

  const selectedFundingAsset = eligibleFundingAssets.find(a => fundingAssetKey(a) === selectedFundingKey)

  // Propose a funding balance once the destination narrows the choices: the
  // largest stable for cash, otherwise the largest eligible balance. An
  // explicit pick always wins.
  useEffect(() => {
    if (selectedFundingAsset || eligibleFundingAssets.length === 0) return

    const pool = flow.kind === 'ivyCashWithdrawal'
      ? eligibleFundingAssets.filter(a => {
        const ticker = getMethod(a.network, a.token).ticker
        return ticker === 'USDC' || ticker === 'USDT'
      })
      : eligibleFundingAssets

    const best = [...(pool.length > 0 ? pool : eligibleFundingAssets)]
      .sort((a, b) => Number(b.maxAmount) - Number(a.maxAmount))[0]
    if (best) {
      setSelectedFundingKey(fundingAssetKey(best))
    }
  }, [eligibleFundingAssets, selectedFundingAsset, flow.kind])

  const selectedFundingMethod = selectedFundingAsset
    ? getMethod(selectedFundingAsset.network, selectedFundingAsset.token)
    : undefined

  const ivyFundingCurrency: PayoutFundingCurrency = (():
  PayoutFundingCurrency => {
    if (!selectedFundingMethod) return 'USDC'
    const ticker = selectedFundingMethod.ticker
    // BNB Chain stablecoins fund over the BEP-20 rail.
    if (selectedFundingAsset?.network === 'BNB' && ticker === 'USDC') return 'USDC-BSC'
    if (selectedFundingAsset?.network === 'BNB' && ticker === 'USDT') return 'USDT-BSC'
    if (ticker === 'USDT') return 'USDT'
    const volatile = VOLATILE_CASH_FUNDING.find(v => v.ticker === ticker)
    return volatile?.ticker ?? 'USDC'
  })()

  const isCashDualEntry = flow.kind === 'ivyCashWithdrawal'
  const isEurRecipient = flow.kind === 'ivyCashWithdrawal' || flow.kind === 'fiatPayout'
  const needsQuote = isEurRecipient || flow.kind === 'cryptoPayout'

  // Live quote for flows the fee endpoint knows about, debounced while typing.
  useEffect(() => {
    if (!needsQuote || !amount || Number(amount) <= 0) {
      setFeeEstimate(null)
      setIsEstimatingFee(false)
      return
    }
    if (flow.kind === 'cryptoPayout' && !selectedFundingMethod) {
      setFeeEstimate(null)
      return
    }

    let cancelled = false
    setIsEstimatingFee(true)
    const timeout = window.setTimeout(() => {
      const request = flow.kind === 'ivyCashWithdrawal'
        ? Radom.getPayoutFee({
          currency: 'EUR',
          amount,
          amountMode,
          accountType: 'business',
          fundingCurrency: ivyFundingCurrency
        })
        : flow.kind === 'fiatPayout'
          ? Radom.getPayoutFee({
            currency: 'EUR',
            amount,
            accountType: flow.externalAccount.accountOwnerType || flow.customer.accountType || 'individual'
          })
          : Radom.getPayoutFee({
            currency: 'USD',
            amount,
            network: selectedFundingMethod?.hostChain?.name ?? selectedFundingMethod?.name,
            token: selectedFundingMethod?.ticker
          })

      request
        .then(estimate => {
          if (!cancelled) setFeeEstimate(estimate)
        })
        .catch(err => {
          console.error('Failed to estimate send fee', err)
          if (!cancelled) setFeeEstimate(null)
        })
        .finally(() => {
          if (!cancelled) setIsEstimatingFee(false)
        })
    }, 300)

    return () => {
      cancelled = true
      window.clearTimeout(timeout)
    }
  }, [needsQuote, flow.kind, amount, amountMode, ivyFundingCurrency, selectedFundingKey])

  const defaultCurrency = User.organization?.defaultCurrency ?? 'USD'

  // Same price-quote state the Funding view uses for balance valuation —
  // gives crypto sends a live approximate value line.
  const approxFiatValue = (ticker: string | undefined, assetAmount: string): number | undefined => {
    if (!ticker || !assetAmount || Number(assetAmount) <= 0) return undefined
    const quote = Treasury.priceQuotes.find(p => p.from.toLowerCase() === ticker.toLowerCase())
    if (!quote) return undefined
    return Number(assetAmount) * quote.value
  }

  const availableUsdcForFiatPayouts = useMemo(() => fundingAssets
    .filter(a => !a.treasuryId &&
      a.network === expectedUsdcNetwork &&
      getMethod(a.network, a.token).ticker === 'USDC')
    .reduce((sum, a) => sum + Number(a.maxAmount), 0), [fundingAssets, expectedUsdcNetwork])

  const estimatedTotalDebit = feeEstimate?.estimatedTotalDebitUsd
  const ivyHasInsufficientFunding = flow.kind === 'ivyCashWithdrawal' &&
    !!estimatedTotalDebit && !!selectedFundingAsset &&
    Number(estimatedTotalDebit) > Number(selectedFundingAsset.maxAmount || 0)
  const fiatPayoutHasInsufficientFunding = flow.kind === 'fiatPayout' &&
    !!estimatedTotalDebit &&
    Number(estimatedTotalDebit) > availableUsdcForFiatPayouts

  const requiredWritePermission = flow.kind === 'cryptoWithdrawal' ||
    flow.kind === 'ivyCashWithdrawal' ||
    flow.kind === 'legacyCashWithdrawal'
    ? 'withdrawal' as const
    : flow.kind === 'cryptoPayout' || flow.kind === 'fiatPayout'
      ? 'payout' as const
      : undefined

  const blockers = useMemo((): string[] => {
    const list: string[] = []
    if (flow.kind === 'idle') {
      list.push('Choose who to send to first.')
      return list
    }
    if (flow.kind === 'blocked') {
      list.push(flow.reason)
      return list
    }
    if (flow.kind === 'customerPending') {
      list.push(isLoadingExternalAccounts
        ? 'Loading this contact’s saved accounts…'
        : 'Choose where this contact should receive the funds.')
      return list
    }

    if (requiredWritePermission === 'withdrawal' && !canWriteWithdrawal) {
      list.push('Your role can’t create withdrawals. Ask an organization admin for the WriteWithdrawal permission.')
    }
    if (requiredWritePermission === 'payout' && !canWritePayouts) {
      list.push('Your role can’t create payouts. Ask an organization admin for the WritePayouts permission.')
    }

    if (flow.kind !== 'fiatPayout') {
      if (eligibleFundingAssets.length === 0) {
        list.push(flow.kind === 'ivyCashWithdrawal'
          ? `EUR bank withdrawals are funded with USDC or USDT on ${expectedUsdcNetwork}. Deposit some first from Add funds.`
          : 'You don’t hold a balance that can fund this destination. Deposit first from Add funds.')
      } else if (!selectedFundingAsset) {
        list.push('Choose the balance to send from.')
      }
    }

    if (flow.kind === 'cryptoPayout' && !flow.customer) {
      const email = oneOffEmail.trim()
      if (!email) {
        list.push('Add the recipient’s email. Payouts are attributed to a recipient record.')
      } else if (!/^\S+@\S+\.\S+$/.test(email)) {
        list.push('Enter a valid recipient email.')
      }
    }

    if (!amount || Number(amount) <= 0) {
      list.push('Enter an amount greater than zero.')
    } else {
      if ((flow.kind === 'cryptoWithdrawal' || flow.kind === 'cryptoPayout' || flow.kind === 'legacyCashWithdrawal') &&
        selectedFundingAsset && selectedFundingMethod &&
        Number(amount) > Number(selectedFundingAsset.maxAmount)) {
        list.push(`Amount exceeds the available ${selectedFundingMethod.ticker} balance.`)
      }
      if (isEurRecipient && !isEstimatingFee && !feeEstimate) {
        list.push('Waiting for a quote before this can be sent.')
      }
      if (isEstimatingFee) {
        list.push('Fetching the latest quote…')
      }
      if (ivyHasInsufficientFunding && selectedFundingMethod) {
        list.push(`Available ${ivyFundingCurrency} balance is lower than the estimated debit.`)
      }
      if (fiatPayoutHasInsufficientFunding) {
        list.push('Your USDC balance is lower than the estimated debit for this payout.')
      }
    }

    return list
  }, [
    flow,
    oneOffEmail,
    requiredWritePermission,
    canWriteWithdrawal,
    canWritePayouts,
    eligibleFundingAssets.length,
    selectedFundingAsset,
    selectedFundingMethod,
    amount,
    isEurRecipient,
    isEstimatingFee,
    feeEstimate,
    ivyHasInsufficientFunding,
    fiatPayoutHasInsufficientFunding,
    isLoadingExternalAccounts,
    expectedUsdcNetwork,
    ivyFundingCurrency
  ])

  const canSend = blockers.length === 0 && !isSubmitting

  const resetFlow = (): void => {
    setDestination(undefined)
    setAmount('')
    setOneOffEmail('')
    setOneOffName('')
    setAmountMode('recipient')
    setFeeEstimate(null)
    setQuoteExpanded(false)
    setSelectedFundingKey(undefined)
    setSelectedExternalAccountId(undefined)
  }

  // Recent one-tap chips: recent withdrawals map back to saved accounts,
  // recent payout entries map back to customers. Anything unmappable (raw
  // addresses, deleted recipients) is skipped rather than guessed at.
  const recentDestinations = useMemo((): SendDestination[] => {
    interface Candidate { timestamp: number, destination: SendDestination }
    const candidates: Candidate[] = []

    for (const withdrawal of recentWithdrawals) {
      const account = accounts.find(a => a.id === withdrawal.withdrawalAccountId)
      if (account) {
        candidates.push({
          timestamp: new Date(withdrawal.requestedAt).getTime(),
          destination: { kind: 'ownAccount', account }
        })
      }
    }

    for (const payout of recentPayouts) {
      const customerIds = [
        ...(payout.cryptoEntries ?? []).map(entry => entry.customerId),
        ...(payout.fiatEntries ?? []).map(entry => entry.customerId)
      ].filter((id): id is string => !!id)
      for (const customerId of customerIds) {
        const customer = customers.find(c => c.id === customerId)
        if (customer) {
          candidates.push({
            timestamp: new Date(payout.createdAt).getTime(),
            destination: { kind: 'customer', customer }
          })
        }
      }
    }

    const seen = new Set<string>()
    const chips: SendDestination[] = []
    for (const candidate of candidates.sort((a, b) => b.timestamp - a.timestamp)) {
      const key = destinationKey(candidate.destination)
      if (seen.has(key)) continue
      seen.add(key)
      chips.push(candidate.destination)
    }
    return chips.slice(0, 5)
  }, [recentWithdrawals, recentPayouts, accounts, customers])

  const recipientAmountLabel = flow.kind === 'ivyCashWithdrawal'
    ? amountMode === 'recipient'
      ? `${formatQuoteAmount(Number(amount) || undefined, 2)} EUR`
      : `${formatQuoteAmount(feeEstimate?.estimatedRecipientAmount, 2)} EUR`
    : flow.kind === 'fiatPayout'
      ? `${formatQuoteAmount(Number(amount) || undefined, 2)} EUR`
      : `${formatAssetAmount(amount || undefined, selectedFundingMethod?.ticker)} ${selectedFundingMethod?.ticker ?? ''}`.trim()
  const payUnit = flow.kind === 'ivyCashWithdrawal' ? ivyFundingCurrency : 'USDC'
  const youPayLabel = isEurRecipient
    ? `${formatQuoteAmount(feeEstimate?.estimatedTotalDebitUsd)} ${payUnit}`
    : recipientAmountLabel

  /** Muted account detail for the review card and success summary. */
  const selectedDestinationDetail = (): string => {
    if (!destination) return ''
    switch (destination.kind) {
    case 'ownAccount':
      return withdrawalAccountSubtitle(destination.account)
    case 'customer':
      return selectedExternalAccount
        ? externalAccountLabel(selectedExternalAccount)
        : destination.customer.email?.trim() || 'Saved contact'
    case 'oneOff':
      return destination.value
    }
  }

  const submitSend = async (): Promise<void> => {
    if (!canSend) return

    const recipientName = (flow.kind === 'cryptoPayout' && !flow.customer && oneOffName.trim()) ||
      (destination ? destinationDisplayName(destination) : 'recipient')
    const recipientDetail = selectedDestinationDetail()
    const sentAmountLabel = recipientAmountLabel

    setIsSubmitting(true)
    try {
      let outcome: SuccessInfo | undefined
      if (flow.kind === 'cryptoWithdrawal' || flow.kind === 'legacyCashWithdrawal') {
        if (!selectedFundingAsset) return
        await Radom.submitWithdrawalRequest(
          selectedFundingAsset.network,
          amount,
          flow.account.id,
          false,
          selectedFundingAsset.token,
          selectedFundingAsset.treasuryId
        )
        outcome = {
          heading: 'Request submitted',
          amountLabel: sentAmountLabel,
          recipientName,
          recipientDetail,
          note: 'Withdrawal request submitted. Follow its progress under withdrawals.',
          historyHref: '/funding?view=withdraw',
          historyLabel: 'View withdrawals'
        }
      } else if (flow.kind === 'ivyCashWithdrawal') {
        const user = await User.getUser()
        const createdPayout = await Radom.createPayout({
          idempotencyKey: cashSubmissionKey.current,
          payouts: [{
            gateway: 'fiatWithdrawalAccount',
            amount,
            amountMode,
            withdrawalAccountId: flow.account.id,
            accountType: 'Business',
            fundingCurrency: ivyFundingCurrency,
            email: user.emailAddress || '',
            name: flow.account.accountDetails.Fiat?.accountOwnerName
          }],
          accountType: 'Business',
          isTestMode: testMode,
          deductFees: false,
          sendEmailReceipts: false
        })
        await Radom.confirmPayout(createdPayout.id)
        cashSubmissionKey.current = crypto.randomUUID()
        outcome = {
          heading: 'Sent',
          amountLabel: sentAmountLabel,
          recipientName,
          recipientDetail,
          note: 'Bank withdrawal submitted. Track it from your payout history.',
          historyHref: ROUTES.PAYOUTS,
          historyLabel: 'View history'
        }
      } else if (flow.kind === 'fiatPayout') {
        const accountType = flow.externalAccount.accountOwnerType || flow.customer.accountType || undefined
        await Radom.createPayout({
          payouts: [{
            gateway: 'fiat',
            amount,
            externalAccountId: flow.externalAccount.id,
            email: flow.customer.email || '',
            name: flow.customer.name || flow.customer.companyName || undefined
          }],
          accountType,
          isTestMode: testMode,
          deductFees: false,
          sendEmailReceipts: false,
          autoConfirm: true
        })
        outcome = {
          heading: 'Sent',
          amountLabel: sentAmountLabel,
          recipientName,
          recipientDetail,
          note: 'Payout created. Track it from your payout history.',
          historyHref: ROUTES.PAYOUTS,
          historyLabel: 'View history'
        }
      } else if (flow.kind === 'cryptoPayout') {
        if (!selectedFundingAsset || !selectedFundingMethod) return
        const customerAccountType = flow.customer?.accountType === 'Business' || flow.customer?.accountType === 'Individual'
          ? flow.customer.accountType
          : undefined
        await Radom.createPayout({
          payouts: [{
            gateway: 'crypto',
            amount,
            payoutAddress: flow.payoutAddress,
            network: selectedFundingAsset.network,
            token: selectedFundingAsset.token,
            customerId: flow.customer?.id,
            accountType: customerAccountType,
            email: flow.customer?.email || oneOffEmail.trim() || undefined,
            name: flow.customer
              ? (flow.customer.name || flow.customer.companyName || undefined)
              : (oneOffName.trim() || undefined)
          }],
          accountType: flow.externalAccount?.accountOwnerType || customerAccountType || undefined,
          isTestMode: testMode,
          deductFees: false,
          sendEmailReceipts: false,
          autoConfirm: true
        })
        outcome = {
          heading: 'Sent',
          amountLabel: sentAmountLabel,
          recipientName,
          recipientDetail,
          note: 'Payout created. Track it from your payout history.',
          historyHref: ROUTES.PAYOUTS,
          historyLabel: 'View history'
        }
      } else {
        return
      }

      setSuccessInfo(outcome)
      setStep('success')
      resetFlow()
      void Treasury.loadBalances()
      void loadActivity()
    } catch (err: any) {
      console.error('Failed to send', err)
      errorToast(err?.response?.data?.error || err?.message || 'Failed to send')
    } finally {
      setIsSubmitting(false)
    }
  }

  const goToReview = (): void => {
    if (blockers.length === 0) {
      setStep('review')
    }
  }

  const renderFundingAssetOption = (asset?: FundingAsset): ReactElement => {
    if (!asset) {
      return <span style={{ color: 'var(--icon-muted, #747894)', fontSize: 13 }}>Select balance</span>
    }

    const method = getMethod(asset.network, asset.token)
    const balanceLabel = `Available ${formatAssetAmount(asset.maxAmount, method.ticker)} ${method.ticker}`
    return <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0, width: '100%' }}>
      <SimpleMethodIcon $logo={method.logo} $size={20} />
      <SelectionMeta>
        <SelectionLabel>{method.ticker} on {method.hostChain?.name ?? method.name}</SelectionLabel>
        <SelectionSubtext>
          {asset.treasuryId ? `Private treasury · ${balanceLabel}` : balanceLabel}
        </SelectionSubtext>
      </SelectionMeta>
    </div>
  }

  const renderFundingAssetSelect = (): ReactElement => {
    const query = assetSearch.trim().toLowerCase()
    const searched = eligibleFundingAssets.filter(asset => {
      if (!query) return true
      const method = getMethod(asset.network, asset.token)
      return [method.ticker, method.name, method.currencyName, method.hostChain?.name, asset.network]
        .some(term => term?.toLowerCase().includes(query))
    })

    return <EscapableDropdown
      matchTriggerWidth
      onClose={() => setAssetSearch('')}
      outerStyle={{ minHeight: 44 }}
      selectedContent={renderFundingAssetOption(selectedFundingAsset)}
      dropdownContent={
        <DropdownContext.Consumer>
          {({ closeDropdown }) => <div style={{ minWidth: 300 }}>
            <DropdownSearchContainer>
              <DropdownTextInput
                value={assetSearch}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => e.stopPropagation()}
                onChange={e => setAssetSearch(e.target.value)}
                placeholder='Search balances'
                style={{
                  border: '1px solid var(--border-strong, #d8dde7)',
                  borderRadius: 10,
                  boxSizing: 'border-box',
                  minHeight: 38,
                  padding: '0 10px'
                }}
                autoFocus />
            </DropdownSearchContainer>
            {searched.map(asset => <DropdownItem
              key={fundingAssetKey(asset)}
              style={{ padding: '11px 12px' }}
              onClick={() => {
                setSelectedFundingKey(fundingAssetKey(asset))
                closeDropdown()
              }}>
              {renderFundingAssetOption(asset)}
            </DropdownItem>)}
            {searched.length === 0 && <div style={{
              color: 'var(--text-soft, #7d8492)',
              fontSize: 12.5,
              lineHeight: 1.45,
              padding: '8px 12px 14px'
            }}>
              No balances match this search.
            </div>}
          </div>}
        </DropdownContext.Consumer>
      } />
  }

  const renderDeliveryOptionText = (account?: ExternalAccount): ReactElement => {
    if (!account) {
      return <span style={{ color: 'var(--icon-muted, #747894)', fontSize: 13 }}>Select account</span>
    }

    return <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0, width: '100%' }}>
      <SelectionMeta>
        <SelectionLabel>{account.accountOwnerName || account.businessName || 'Saved account'}</SelectionLabel>
        <SelectionSubtext>{externalAccountLabel(account)}</SelectionSubtext>
      </SelectionMeta>
      <div style={{ flexGrow: 1 }} />
      <InlinePill>{account.crypto ? 'Wallet' : 'Bank'}</InlinePill>
    </div>
  }

  const renderDeliverySelect = (): ReactElement | null => {
    if (destination?.kind !== 'customer') return null

    return <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <SectionLabel>Deliver to</SectionLabel>
      {
        isLoadingExternalAccounts
          ? <LoadingBar style={{ borderRadius: 25, width: '100%', height: 44 }} />
          : deliveryOptions.length > 0
            ? <EscapableDropdown
              matchTriggerWidth
              outerStyle={{ minHeight: 44 }}
              selectedContent={renderDeliveryOptionText(selectedExternalAccount)}
              dropdownContent={
                <DropdownContext.Consumer>
                  {({ closeDropdown }) => <div>
                    {deliveryOptions.map(account => <DropdownItem
                      key={account.id}
                      style={{ padding: '11px 12px' }}
                      onClick={() => {
                        setSelectedExternalAccountId(account.id)
                        setSelectedFundingKey(undefined)
                        setAmount('')
                        pendingAmountFocus.current = true
                        closeDropdown()
                      }}>
                      {renderDeliveryOptionText(account)}
                    </DropdownItem>)}
                  </div>}
                </DropdownContext.Consumer>
              } />
            : <EmptyState style={{ minHeight: 84 }}>
              This contact has no saved bank account or wallet yet. Add one from their page under Contacts.
            </EmptyState>
      }
    </div>
  }

  const renderQuoteDetails = (): ReactElement | null => {
    if (!feeEstimate) return null

    const hasFundingError = ivyHasInsufficientFunding || fiatPayoutHasInsufficientFunding
    return <QuoteBox $error={hasFundingError}>
      {isEurRecipient
        ? <>
          <QuoteRow>
            <QuoteLabel>Recipient gets</QuoteLabel>
            <QuoteValue>
              {formatQuoteAmount(
                amountMode === 'source' && flow.kind === 'ivyCashWithdrawal'
                  ? feeEstimate.estimatedRecipientAmount
                  : Number(amount),
                2
              )} EUR
            </QuoteValue>
          </QuoteRow>
          <QuoteRow>
            <QuoteLabel>You pay</QuoteLabel>
            <QuoteValue>{formatQuoteAmount(feeEstimate.estimatedTotalDebitUsd)} {payUnit}</QuoteValue>
          </QuoteRow>
          <QuoteRow>
            <QuoteLabel>Fee</QuoteLabel>
            <QuoteValue>{formatQuoteAmount(feeEstimate.estimatedFeeUsd)} {payUnit}</QuoteValue>
          </QuoteRow>
          <QuoteRow>
            <QuoteLabel>Rate</QuoteLabel>
            <QuoteValue>1 {payUnit} ≈ {formatQuoteAmount(feeEstimate.exchangeRate, 8)} EUR</QuoteValue>
          </QuoteRow>
        </>
        : <>
          <QuoteRow>
            <QuoteLabel>Recipient gets</QuoteLabel>
            <QuoteValue>
              {formatAssetAmount(amount, selectedFundingMethod?.ticker)} {selectedFundingMethod?.ticker}
            </QuoteValue>
          </QuoteRow>
          <QuoteRow>
            <QuoteLabel>Estimated fee</QuoteLabel>
            <QuoteValue>{formatQuoteAmount(feeEstimate.estimatedFeeUsd, 2)} USD</QuoteValue>
          </QuoteRow>
        </>}
    </QuoteBox>
  }

  const renderQuoteLine = (): ReactElement | null => {
    if (!needsQuote || !amount || Number(amount) <= 0) return null

    return <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {
        isEstimatingFee
          ? <LoadingBar style={{ borderRadius: 25, width: 220, maxWidth: '100%', height: 18 }} />
          : !feeEstimate
            ? <span style={{ color: 'var(--text-faint, #8a919e)', fontSize: 12.5 }}>
              A live quote appears here once the amount can be priced.
            </span>
            : <QuoteLineButton
              type='button'
              aria-expanded={quoteExpanded}
              onClick={() => setQuoteExpanded(expanded => !expanded)}>
              {isEurRecipient
                ? <span>
                  Fee {formatQuoteAmount(feeEstimate.estimatedFeeUsd)} {payUnit}
                  {' · '}1 {payUnit} ≈ {formatQuoteAmount(feeEstimate.exchangeRate, 4)} EUR
                  {' · '}All fees included
                </span>
                : <span>Estimated fee {formatQuoteAmount(feeEstimate.estimatedFeeUsd, 2)} USD</span>}
              <Chevron fill='black' style={{
                opacity: 0.3,
                transition: '0.2s ease transform',
                transform: quoteExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                minWidth: 13,
                width: 13
              }} />
            </QuoteLineButton>
      }
      {quoteExpanded && !isEstimatingFee && renderQuoteDetails()}
      {ivyHasInsufficientFunding && <span style={{ color: '#b42318', fontSize: 12.25, lineHeight: 1.4 }}>
        Available {ivyFundingCurrency} balance is lower than the estimated debit.
      </span>}
      {fiatPayoutHasInsufficientFunding && <span style={{ color: '#b42318', fontSize: 12.25, lineHeight: 1.4 }}>
        Your USDC balance is lower than the estimated debit for this payout.
      </span>}
    </div>
  }

  const renderAmountEntry = (): ReactElement => {
    const unitLabel = flow.kind === 'ivyCashWithdrawal'
      ? amountMode === 'recipient' ? 'EUR' : ivyFundingCurrency
      : flow.kind === 'fiatPayout'
        ? 'EUR'
        : selectedFundingMethod?.ticker ?? ''
    const amountLabelText = flow.kind === 'ivyCashWithdrawal'
      ? amountMode === 'recipient' ? 'Recipient gets' : 'You pay'
      : flow.kind === 'fiatPayout'
        ? 'Recipient gets'
        : flow.kind === 'legacyCashWithdrawal' ? 'Funding amount' : 'Amount'
    const showMax = flow.kind === 'cryptoWithdrawal' ||
      flow.kind === 'cryptoPayout' ||
      flow.kind === 'legacyCashWithdrawal' ||
      (flow.kind === 'ivyCashWithdrawal' && amountMode === 'source')
    const approx = flow.kind === 'cryptoWithdrawal' || flow.kind === 'cryptoPayout' || flow.kind === 'legacyCashWithdrawal'
      ? approxFiatValue(selectedFundingMethod?.ticker, amount)
      : undefined

    return <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between' }}>
        <SectionLabel>{amountLabelText}</SectionLabel>
        {isCashDualEntry && <LightSegmented role='group' aria-label='Amount side'>
          {[
            { key: 'recipient' as PayoutAmountMode, label: 'Recipient gets' },
            { key: 'source' as PayoutAmountMode, label: 'You pay' }
          ].map(option => <LightSegmentedButton
            key={option.key}
            type='button'
            aria-pressed={amountMode === option.key}
            $active={amountMode === option.key}
            onClick={() => {
              setAmountMode(option.key)
              setAmount('')
              pendingAmountFocus.current = true
            }}>
            {option.label}
          </LightSegmentedButton>)}
        </LightSegmented>}
      </div>

      <HeroAmountRow onClick={() => amountInputRef.current?.focus()}>
        <HeroAmountField
          ref={amountInputRef}
          value={amount}
          $chars={amount.length || 1}
          type='text'
          inputMode='decimal'
          autoComplete='off'
          autoCorrect='off'
          spellCheck={false}
          maxLength={30}
          placeholder='0'
          aria-label={amountLabelText}
          disabled={isSubmitting}
          onChange={e => {
            if (/^\d*\.?\d*$/.test(e.target.value)) {
              setAmount(e.target.value)
            }
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.defaultPrevented) {
              e.preventDefault()
              goToReview()
            }
          }} />
        {unitLabel && <HeroCurrencySuffix onClick={() => amountInputRef.current?.focus()}>
          {unitLabel}
        </HeroCurrencySuffix>}
      </HeroAmountRow>

      <HeroHelperRow>
        {showMax && selectedFundingAsset && selectedFundingMethod && <>
          <span>
            Available {formatAssetAmount(selectedFundingAsset.maxAmount, selectedFundingMethod.ticker)}{' '}
            {selectedFundingMethod.ticker}
          </span>
          <span aria-hidden='true'>·</span>
          <MaxTextButton
            type='button'
            disabled={isSubmitting}
            onClick={() => setAmount(selectedFundingAsset.maxAmount)}>
            Max
          </MaxTextButton>
        </>}
        {flow.kind === 'fiatPayout' && <span>
          Funded from your USDC balance on {expectedUsdcNetwork} ·{' '}
          {formatAssetAmount(String(availableUsdcForFiatPayouts), 'USDC')} USDC available
        </span>}
        {approx !== undefined && <span>≈ {formatQuoteAmount(approx, 2)} {defaultCurrency}</span>}
      </HeroHelperRow>

      {renderQuoteLine()}
    </div>
  }

  const renderIbanRecipientCard = (iban: string): ReactElement => (
    <RecipientCard>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <RecipientCardTitle>Who does this account belong to?</RecipientCardTitle>
        <RecipientCardHint>
          Bank payouts deliver to saved recipients. Save the owner of ····{iban.slice(-4)} once.
          The account is verified with their bank, and the payout continues right here.
        </RecipientCardHint>
      </div>

      <LightSegmented role='group' aria-label='Recipient type' style={{ alignSelf: 'flex-start' }}>
        {[
          { key: 'individual' as const, label: 'Person' },
          { key: 'business' as const, label: 'Business' }
        ].map(option => <LightSegmentedButton
          key={option.key}
          type='button'
          aria-pressed={recipientOwnerType === option.key}
          $active={recipientOwnerType === option.key}
          onClick={() => setRecipientOwnerType(option.key)}>
          {option.label}
        </LightSegmentedButton>)}
      </LightSegmented>

      {recipientOwnerType === 'individual'
        ? <RecipientFieldsGrid>
          <TextInput
            placeholder='First name'
            value={recipientFirstName}
            disabled={isSavingRecipient}
            onChange={e => setRecipientFirstName((e.target as HTMLInputElement).value)} />
          <TextInput
            placeholder='Last name'
            value={recipientLastName}
            disabled={isSavingRecipient}
            onChange={e => setRecipientLastName((e.target as HTMLInputElement).value)} />
        </RecipientFieldsGrid>
        : <TextInput
          placeholder='Business name on the bank account'
          value={recipientBusinessName}
          disabled={isSavingRecipient}
          onChange={e => setRecipientBusinessName((e.target as HTMLInputElement).value)} />}

      <RecipientFieldsGrid>
        <TextInput
          placeholder='Email'
          inputMode='email'
          value={recipientEmail}
          disabled={isSavingRecipient}
          onChange={e => setRecipientEmail((e.target as HTMLInputElement).value)} />
        <TextInput
          placeholder='BIC (optional)'
          value={recipientBic}
          disabled={isSavingRecipient}
          onChange={e => setRecipientBic((e.target as HTMLInputElement).value)} />
      </RecipientFieldsGrid>

      <RecipientCardHint>
        The name must match the bank account exactly. Banks verify the payee before accepting transfers.
      </RecipientCardHint>

      <PrimaryButton
        isLoading={isSavingRecipient}
        style={{ alignSelf: 'flex-start', padding: '10px 18px' }}
        onClick={() => { saveIbanRecipient(iban) }}>
        Save recipient and continue
      </PrimaryButton>
    </RecipientCard>
  )

  const renderWalletRecipientCard = (address: string, candidates: string[]): ReactElement => (
    <RecipientCard>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <RecipientCardTitle>Save this wallet to send to it</RecipientCardTitle>
        <RecipientCardHint>
          New crypto destinations are saved once as wallet accounts.
          Withdrawals to ····{address.slice(-6)} continue right here.
        </RecipientCardHint>
      </div>

      {candidates.length > 1 &&
        <LightSegmented role='group' aria-label='Network' style={{ alignSelf: 'flex-start' }}>
          {candidates.map(network => {
            const method = getMethod(network)
            return <LightSegmentedButton
              key={network}
              type='button'
              aria-pressed={walletRecipientNetwork === network}
              $active={walletRecipientNetwork === network}
              onClick={() => {
                setWalletRecipientNetwork(network)
                setWalletRecipientName(prev =>
                  !prev.trim() || /^[A-Z0-9.]+ wallet$/.test(prev) ? `${method.ticker} wallet` : prev)
              }}>
              <img src={method.logo} style={{ height: 15, width: 15 }} alt='' />
              {method.currencyName ?? method.name}
            </LightSegmentedButton>
          })}
        </LightSegmented>}

      {candidates.length === 1 && (() => {
        const method = getMethod(candidates[0])
        return <RecipientCardHint style={{ alignItems: 'center', display: 'flex', gap: 7 }}>
          <img src={method.logo} style={{ height: 15, width: 15 }} alt='' />
          <span>{method.currencyName ?? method.name} network</span>
        </RecipientCardHint>
      })()}

      <TextInput
        placeholder='Wallet nickname'
        value={walletRecipientName}
        disabled={isSavingRecipient}
        style={{ maxWidth: 320 }}
        onChange={e => setWalletRecipientName((e.target as HTMLInputElement).value)} />

      <PrimaryButton
        isLoading={isSavingRecipient}
        style={{ alignSelf: 'flex-start', padding: '10px 18px' }}
        onClick={() => { saveWalletRecipient(address) }}>
        Save wallet and continue
      </PrimaryButton>
    </RecipientCard>
  )

  const destinationChosen = flow.kind !== 'idle'
  const flowIsActive = destinationChosen &&
    flow.kind !== 'blocked' &&
    flow.kind !== 'customerPending' &&
    flow.kind !== 'ibanRecipient' &&
    flow.kind !== 'walletRecipient'
  const showAmountArea = flowIsActive &&
    (flow.kind === 'fiatPayout' || eligibleFundingAssets.length > 0)

  const renderFormStep = (): ReactElement => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <SectionLabel>To</SectionLabel>
        <DestinationPicker
          accounts={accounts}
          customers={customers}
          isLoading={isLoadingRecipients}
          canReadAccounts={canReadAccounts}
          canReadCustomers={canReadCustomers}
          canCreateAccounts={canWriteAccounts}
          selected={destination}
          recent={recentDestinations}
          onSelect={d => {
            pendingAmountFocus.current = true
            setDestination(d)
          }}
          onClear={() => setDestination(undefined)}
          onCreateAccount={kind =>
            setCreateAccountType(kind === 'bank' ? AccountType.Fiat : AccountType.Crypto)} />
        {(canWriteAccounts || (canReadAccounts && accounts.length > 0)) && !destination &&
          <QuickActionsRow>
            {canWriteAccounts && <>
              <QuickActionPill
                type='button'
                onClick={() => setCreateAccountType(AccountType.Fiat)}>
                <QuickActionGlyph>+</QuickActionGlyph> New bank account
              </QuickActionPill>
              <QuickActionPill
                type='button'
                onClick={() => setCreateAccountType(AccountType.Crypto)}>
                <QuickActionGlyph>+</QuickActionGlyph> New wallet address
              </QuickActionPill>
            </>}
            {canReadAccounts && accounts.length > 0 &&
              <QuickActionPill type='button' onClick={() => navigate('/accounts')}>
                Manage saved accounts
              </QuickActionPill>}
          </QuickActionsRow>}
        {!isLoadingRecipients && accounts.length === 0 && customers.length === 0 && !destination &&
          <InlineNotice style={{ fontSize: 12 }}>
            {canWriteAccounts
              ? 'No saved recipients yet. Create a bank account or wallet address with the buttons above, or paste an address or IBAN to send one-off.'
              : 'No saved recipients yet. Paste an address or IBAN above to send one-off.'}
          </InlineNotice>}
      </div>

      {flow.kind === 'blocked' && <BlockerNotice>{flow.reason}</BlockerNotice>}

      {flow.kind === 'ibanRecipient' && renderIbanRecipientCard(flow.iban)}
      {flow.kind === 'walletRecipient' && renderWalletRecipientCard(flow.address, flow.candidates)}

      {(flow.kind === 'customerPending' || destination?.kind === 'customer') &&
        flow.kind !== 'blocked' &&
        renderDeliverySelect()}

      {flowIsActive && <StepReveal key={destination ? destinationKey(destination) : 'none'}>
        {!showAmountArea && <EmptyState style={{ minHeight: 84 }}>
          {flow.kind === 'ivyCashWithdrawal'
            ? `EUR bank withdrawals are funded with USDC or USDT on ${expectedUsdcNetwork}. Deposit some first from Add funds.`
            : 'No balance can fund this destination yet. Deposit first from Add funds.'}
        </EmptyState>}

        {showAmountArea && renderAmountEntry()}

        {showAmountArea && flow.kind === 'cryptoPayout' && !flow.customer && <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8
        }}>
          <SectionLabel>Recipient details</SectionLabel>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: '1 1 180px' }}>
              <span style={{ color: 'var(--text-soft, #7d8492)', fontSize: 12 }}>Name <span style={{ color: 'var(--text-disabled, #a4aab5)' }}>Optional</span></span>
              <TextInput
                value={oneOffName}
                placeholder='Recipient name'
                disabled={isSubmitting}
                onChange={e => setOneOffName(e.target.value)} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: '1 1 220px' }}>
              <span style={{ color: 'var(--text-soft, #7d8492)', fontSize: 12 }}>Email</span>
              <TextInput
                value={oneOffEmail}
                type='email'
                placeholder='name@company.com'
                autoComplete='off'
                spellCheck={false}
                disabled={isSubmitting}
                onChange={e => setOneOffEmail(e.target.value)} />
            </div>
          </div>
          <span style={{ color: 'var(--text-faint, #8a919e)', fontSize: 12 }}>
            The payout is attributed to this recipient, groundwork for the travel-rule details
            outbound transfers will require.
          </span>
        </div>}

        {showAmountArea && flow.kind !== 'fiatPayout' && <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8
        }}>
          <SectionLabel>
            {flow.kind === 'ivyCashWithdrawal' || flow.kind === 'legacyCashWithdrawal' ? 'Pay with' : 'Send from'}
          </SectionLabel>
          {renderFundingAssetSelect()}
        </div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <PrimaryButton
            disabled={blockers.length > 0 || isSubmitting}
            style={{ width: '100%' }}
            onClick={goToReview}>
            Continue
          </PrimaryButton>
          {blockers.length > 0 && <BlockerNotice>{blockers[0]}</BlockerNotice>}
        </div>
      </StepReveal>}
    </div>
  )

  const renderReviewStep = (): ReactElement => {
    const mode = destination ? destinationMode(destination) : 'payout'
    const recipientName = (flow.kind === 'cryptoPayout' && !flow.customer && oneOffName.trim()) ||
      (destination ? destinationDisplayName(destination) : '-')

    return <StepReveal>
      <BackTextButton
        type='button'
        disabled={isSubmitting}
        onClick={() => {
          pendingAmountFocus.current = true
          setStep('form')
        }}>
        <span aria-hidden='true'>←</span> Edit
      </BackTextButton>

      <ReviewIdentity>
        <RecipientAvatar name={recipientName} size={48} />
        <ReviewName>{recipientName}</ReviewName>
        <ReviewDetail>{selectedDestinationDetail()}</ReviewDetail>
      </ReviewIdentity>

      <ReviewAmount aria-label='Amount the recipient gets'>{recipientAmountLabel}</ReviewAmount>

      <QuoteBox>
        {flow.kind === 'cryptoPayout' && flow.oneOffKind && <QuoteRow>
          <QuoteLabel>Destination type</QuoteLabel>
          <QuoteValue>{oneOffKindLabel(flow.oneOffKind)} · one-off</QuoteValue>
        </QuoteRow>}
        {!isEurRecipient && selectedFundingMethod && <QuoteRow>
          <QuoteLabel>From</QuoteLabel>
          <QuoteValue>
            {selectedFundingMethod.ticker} on{' '}
            {selectedFundingMethod.hostChain?.name ?? selectedFundingMethod.name}
          </QuoteValue>
        </QuoteRow>}
        {feeEstimate && <QuoteRow>
          <QuoteLabel>Fee</QuoteLabel>
          <QuoteValue>
            {formatQuoteAmount(feeEstimate.estimatedFeeUsd)}{' '}
            {isEurRecipient ? payUnit : 'USD'}
          </QuoteValue>
        </QuoteRow>}
        {isEurRecipient && feeEstimate?.exchangeRate != null && <QuoteRow>
          <QuoteLabel>Rate</QuoteLabel>
          <QuoteValue>1 {payUnit} ≈ {formatQuoteAmount(feeEstimate.exchangeRate, 8)} EUR</QuoteValue>
        </QuoteRow>}
        <QuoteRow>
          <QuoteLabel>Total debit</QuoteLabel>
          <QuoteValue>{youPayLabel}</QuoteValue>
        </QuoteRow>
        <QuoteRow>
          <QuoteLabel>Books as</QuoteLabel>
          <QuoteValue>
            <InlinePill>{mode === 'withdrawal' ? 'Withdrawal' : 'Payout'}</InlinePill>
          </QuoteValue>
        </QuoteRow>
      </QuoteBox>

      <div ref={reviewActionsRef} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <PrimaryButton
          disabled={!canSend}
          isLoading={isSubmitting}
          style={{ width: '100%' }}
          onClick={submitSend}>
          Send
        </PrimaryButton>
        {blockers.length > 0 && <BlockerNotice>{blockers[0]}</BlockerNotice>}
      </div>
    </StepReveal>
  }

  const renderSuccessStep = (info: SuccessInfo): ReactElement => (
    <StepReveal>
      <SuccessPanel role='status'>
        <SuccessBadge><CheckMark /></SuccessBadge>
        <SuccessHeading>{info.heading}</SuccessHeading>
        <SuccessSummary>{info.amountLabel} to {info.recipientName}</SuccessSummary>
        {info.recipientDetail && <SuccessNote>{info.recipientDetail}</SuccessNote>}
        <SuccessNote>{info.note}</SuccessNote>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', paddingTop: 16 }}>
          <PrimaryButton
            onClick={() => {
              setSuccessInfo(undefined)
              setStep('form')
            }}>
            Send another
          </PrimaryButton>
          <SecondaryLinkButton to={info.historyHref}>
            {info.historyLabel}
          </SecondaryLinkButton>
        </div>
      </SuccessPanel>
    </StepReveal>
  )

  const activityRows = useMemo((): ActivityRow[] => {
    const withdrawalRows: ActivityRow[] = recentWithdrawals.map(withdrawal => {
      const method = getMethod(withdrawal.network, withdrawal.token)
      return {
        id: `withdrawal-${withdrawal.id}`,
        title: withdrawal.withdrawalAccountName ??
          shortAddress(withdrawal.withdrawalAddress || withdrawal.withdrawalAccountId, 6),
        meta: 'Withdrawal',
        amountLabel: `${formatAssetAmount(withdrawal.withdrawalAmount, method.ticker)} ${method.ticker}`,
        statusLabel: withdrawalStatusLabel(withdrawal.withdrawalStatus),
        tone: withdrawalStatusTone(withdrawal.withdrawalStatus),
        timestamp: new Date(withdrawal.requestedAt).getTime(),
        href: `/transactions?selected=${withdrawal.id}`,
        method: { network: withdrawal.network, token: withdrawal.token }
      }
    })

    const payoutRows: ActivityRow[] = recentPayouts.map(payout => {
      // Label single-entry payouts in what the recipient actually gets;
      // only multi-entry batches fall back to the USD total.
      let amountLabel = `$${formatQuoteAmount(payout.totalAmountUsd, 2)}`
      let rowMethod: ActivityRow['method']
      if (payout.entryCount === 1) {
        const fiat = payout.fiatEntries?.[0]
        const crypto = payout.cryptoEntries?.[0]
        if (fiat) {
          amountLabel = `${formatQuoteAmount(fiat.amount, 2)} ${fiat.currency?.toUpperCase() ?? ''}`.trim()
          rowMethod = { network: 'Fiat', token: fiat.currency?.toUpperCase() }
        } else if (crypto) {
          const method = getMethod(crypto.network, crypto.token)
          amountLabel = `${formatAssetAmount(crypto.amount, method.ticker)} ${method.ticker}`
          rowMethod = { network: crypto.network, token: crypto.token }
        }
      } else {
        const firstEntry = payout.cryptoEntries?.[0] ?? payout.fiatEntries?.[0]
        if (firstEntry && 'network' in firstEntry) {
          rowMethod = { network: firstEntry.network, token: firstEntry.token }
        }
      }
      return {
        id: `payout-${payout.id}`,
        title: payout.entryCount > 1 ? `Payout · ${payout.entryCount} recipients` : 'Payout',
        meta: 'Payout',
        amountLabel,
        statusLabel: payoutStatusLabel(payout.status),
        tone: payoutStatusTone(payout.status),
        timestamp: new Date(payout.createdAt).getTime(),
        href: `/payout/${payout.id}`,
        method: rowMethod
      }
    })

    return [...withdrawalRows, ...payoutRows]
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 8)
  }, [recentWithdrawals, recentPayouts])

  const renderActivity = (): ReactElement => (
    <Surface $compact>
      <SurfaceHeader>
        <div>
          <SurfaceTitle>Recent sends</SurfaceTitle>
          <SurfaceSubtext>Withdrawals and payouts across both rails.</SurfaceSubtext>
        </div>
      </SurfaceHeader>
      {
        isLoadingActivity
          ? <LoadingBar style={{ borderRadius: 25, width: '100%', height: 68 }} />
          : activityRows.length === 0
            ? <EmptyState style={{ minHeight: 96 }}>
              Nothing sent yet. Your withdrawals and payouts will show up here.
            </EmptyState>
            : <div style={{ display: 'flex', flexDirection: 'column' }}>
              {activityRows.map(row => <ActivityRowLink
                key={row.id}
                to={row.href}>
                <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0 }}>
                  {row.method && <PaymentMethodDisplay
                    method={row.method}
                    excludeName
                    size={22} />}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                    <SelectionLabel style={{ fontSize: 13 }}>{row.title}</SelectionLabel>
                    <SelectionSubtext style={{ fontSize: 11.5, fontVariantNumeric: 'tabular-nums' }}>
                      {formatDate(new Date(row.timestamp), 'd MMM, HH:mm')} · {row.amountLabel}
                    </SelectionSubtext>
                  </div>
                </div>
                <div style={{ alignItems: 'flex-end', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <InlinePill $tone={row.tone}>{row.statusLabel}</InlinePill>
                  <SelectionSubtext style={{ fontSize: 11 }}>{row.meta}</SelectionSubtext>
                </div>
              </ActivityRowLink>)}
            </div>
      }
    </Surface>
  )

  const renderSendWorkspace = (): ReactElement => (
    <FlowSurface>
      {step === 'form' && renderFormStep()}
      {step === 'review' && renderReviewStep()}
      {step === 'success' && successInfo && renderSuccessStep(successInfo)}
    </FlowSurface>
  )

  if (permissionsLoaded && !canUseView) {
    return <Container>
      <DashboardPage>
        <PageHeaderRow>
          <PageHeader title='Send funds' />
        </PageHeaderRow>
        <EmptyState style={{ minHeight: 160 }}>
          You do not have permission to send funds. Ask an organization admin to grant your role
          access to withdrawals or payouts.
        </EmptyState>
      </DashboardPage>
    </Container>
  }

  return <Container>
    <DashboardPage>
      <PageHeaderRow>
        <PageHeader title='Send funds' />
        <PageActionRow>
          <SecondaryLinkButton
            to={ROUTES.PAYOUTS}
            style={{ fontSize: 13, minHeight: 34, padding: '0 12px' }}>
            Payout history
          </SecondaryLinkButton>
          <InlinePill $tone={testMode ? 'amber' : 'green'}>{testMode ? 'Test mode' : 'Live mode'}</InlinePill>
        </PageActionRow>
      </PageHeaderRow>

      <CenteredWorkspace>
        <SendLayout>
          {renderSendWorkspace()}
          {renderActivity()}
        </SendLayout>
      </CenteredWorkspace>
    </DashboardPage>

    {createAccountType !== undefined && <CreateWithdrawalAccountModal
      title={createAccountType === AccountType.Fiat ? 'New bank account' : 'New wallet address'}
      visible
      presentation='drawer'
      accountType={createAccountType}
      onClose={() => setCreateAccountType(undefined)}
      onAccountCreated={() => { void handleAccountCreated() }}
    />}
  </Container>
})

export default Send
